import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Date.prototype.toString.call(Reflect.construct(Date, [], function () {})); return true; } catch (e) { return false; } }

import React from 'react';
import PropTypes from 'prop-types';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import { isSameDay, setYear, setMonth, setDate } from 'date-fns';
import Popover from '../Popover';
import Calendar from '../Calendar';
import DateInput from './DateInput';
import { PopoverCommonProps } from '../common/PropTypes/PopoverCommon';
import deprecationLog from '../utils/deprecationLog';
import styles from './DatePicker.st.css';
/**
 * DatePicker component
 *
 * ### Keyboard support
 * * `Left`: Move to the previous day.
 * * `Right`: Move to the next day.
 * * `Up`: Move to the previous week.
 * * `Down`: Move to the next week.
 * * `PgUp`: Move to the previous month.
 * * `PgDn`: Move to the next month.
 * * `Home`: Move to the previous year.
 * * `End`: Move to the next year.
 * * `Enter`/`Esc`/`Tab`: close the calendar. (`Enter` & `Esc` calls `preventDefault`)
 *
 */

var DatePicker = /*#__PURE__*/function (_React$PureComponent) {
  _inherits(DatePicker, _React$PureComponent);

  var _super = _createSuper(DatePicker);

  function DatePicker(_props) {
    var _this;

    _classCallCheck(this, DatePicker);

    _this = _super.call(this, _props);

    _defineProperty(_assertThisInitialized(_this), "openCalendar", function () {
      if (!_this.state.isOpen) {
        _this.setState({
          isOpen: true,
          isDateInputFocusable: false,
          value: _this.props.value || new Date()
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "closeCalendar", function () {
      _this.setState({
        isOpen: false
      }, function () {
        if (_this.props.onClose) {
          _this.props.onClose();
        }
      });
      /*
        to fix case when user press tab in opened Calendar and:
          1. Calendar become closed
          2. Focus triggered
          3. openCalendar triggered by focus
          4. Calendar become visible
          5. Looks like nothing happen
        We need to do such steps:
         1. Close calendar(with isDateInputFocusable: false)
         2. After calendar is closed, on next event loop(after focus is fired)  make isDateInputFocusable: focusable
         to allow user to press tab in future and open Calendar
      */


      setTimeout(function () {
        return _this.makeInputFocusable();
      });
    });

    _defineProperty(_assertThisInitialized(_this), "makeInputFocusable", function () {
      return _this.setState({
        isDateInputFocusable: true
      });
    });

    _defineProperty(_assertThisInitialized(_this), "_saveNewValue", function (value) {
      var modifiers = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      if (modifiers.disabled) {
        return;
      }

      var isChanged = !isSameDay(value, _this.props.value);

      if (isChanged) {
        var oldValue = _this.props.value || new Date(new Date().setHours(0, 0, 0, 0));
        var newValue = [[value.getFullYear(), setYear], [value.getMonth(), setMonth], [value.getDate(), setDate]].reduce(function (_value, _ref) {
          var _ref2 = _slicedToArray(_ref, 2),
              datePart = _ref2[0],
              setter = _ref2[1];

          return setter(_value, datePart);
        }, oldValue);

        _this.setState({
          value: newValue
        }, function () {
          return _this.props.onChange(newValue);
        });
      }
    });

    _defineProperty(_assertThisInitialized(_this), "_handleKeyDown", function (event) {
      // TODO: dirty for now
      // tab key should move focus so can't preventDefault
      if (event.keyCode !== 9) {
        event.preventDefault();
      }

      if (!_this.state.isOpen) {
        _this.openCalendar();
      } // keyHandler(this.state.value);

    });

    _defineProperty(_assertThisInitialized(_this), "_renderInputWithRefForward", function () {
      return /*#__PURE__*/React.forwardRef(function (props, ref) {
        return _this._renderInput(_objectSpread(_objectSpread({}, props), {}, {
          ref: ref
        }));
      });
    });

    _defineProperty(_assertThisInitialized(_this), "_renderInput", function () {
      var _this$props = _this.props,
          inputDataHook = _this$props.inputDataHook,
          disabled = _this$props.disabled,
          placeholderText = _this$props.placeholderText,
          readOnly = _this$props.readOnly,
          initialValue = _this$props.value,
          status = _this$props.status,
          statusMessage = _this$props.statusMessage,
          customInput = _this$props.customInput,
          dateFormatV2 = _this$props.dateFormatV2,
          dateFormat = _this$props.dateFormat,
          _this$props$inputProp = _this$props.inputProps,
          inputProps = _this$props$inputProp === void 0 ? {} : _this$props$inputProp;

      var _onFocus = inputProps.onFocus,
          inputPropsRest = _objectWithoutProperties(inputProps, ["onFocus"]);

      return /*#__PURE__*/React.createElement(DateInput, _extends({
        dataHook: inputDataHook,
        value: initialValue
        /* This line normally does nothing, because once clicked, component is already focused, hence onFocus
        kicks in and open the calendar.
        Why do we still keep this line? Backward compatibility for clients that test the component and simulate click
        without simulating focus first. */
        ,
        onInputClicked: _this.openCalendar,
        disabled: disabled,
        readOnly: readOnly,
        placeholder: placeholderText,
        onFocus: function onFocus(e) {
          _onFocus && _onFocus(e);

          _this.openCalendar(e);
        },
        onKeyDown: _this._handleKeyDown,
        tabIndex: _this.state.isDateInputFocusable ? 0 : -1,
        status: status,
        statusMessage: statusMessage,
        autoSelect: false,
        dateFormat: dateFormat,
        dateFormatV2: dateFormatV2,
        customInput: customInput
      }, customInput ? customInput.props : {}, inputPropsRest));
    });

    var initialOpen = _props.initialOpen && !_props.disabled;
    _this.state = {
      value: _props.value || new Date(),
      isOpen: initialOpen,
      isDateInputFocusable: !_props.initialOpen
    };
    deprecationLog('dateFormat prop is deprecated and will be removed as part of the next major version, please use dateFormatV2');
    return _this;
  }

  _createClass(DatePicker, [{
    key: "onClickOutside",
    value: function onClickOutside() {
      this.closeCalendar();
    }
  }, {
    key: "render",
    value: function render() {
      var _this$props2 = this.props,
          showMonthDropdown = _this$props2.showMonthDropdown,
          showYearDropdown = _this$props2.showYearDropdown,
          filterDate = _this$props2.filterDate,
          excludePastDates = _this$props2.excludePastDates,
          rtl = _this$props2.rtl,
          shouldCloseOnSelect = _this$props2.shouldCloseOnSelect,
          width = _this$props2.width,
          calendarDataHook = _this$props2.calendarDataHook,
          twoMonths = _this$props2.twoMonths,
          locale = _this$props2.locale,
          zIndex = _this$props2.zIndex,
          dataHook = _this$props2.dataHook,
          popoverProps = _this$props2.popoverProps,
          firstDayOfWeek = _this$props2.firstDayOfWeek;
      var _this$state = this.state,
          isOpen = _this$state.isOpen,
          value = _this$state.value;
      var calendarProps = {
        dataHook: 'date-picker-calendar',
        locale: locale,
        showMonthDropdown: showMonthDropdown,
        showYearDropdown: showYearDropdown,
        filterDate: filterDate,
        excludePastDates: excludePastDates,
        rtl: rtl,
        onChange: this._saveNewValue,
        onClose: this.closeCalendar,
        value: value,
        shouldCloseOnSelect: shouldCloseOnSelect,
        numOfMonths: twoMonths ? 2 : 1,
        firstDayOfWeek: firstDayOfWeek
      };
      return /*#__PURE__*/React.createElement(Popover, _extends({}, styles('root', {}, this.props), {
        dataHook: dataHook,
        onClickOutside: this.closeCalendar,
        appendTo: "parent",
        shown: isOpen,
        zIndex: zIndex
      }, popoverProps), /*#__PURE__*/React.createElement(Popover.Element, null, /*#__PURE__*/React.createElement("div", {
        style: {
          width: width
        },
        "data-hook": "date-picker-input-container"
      }, /*#__PURE__*/React.createElement(DayPickerInput, {
        component: this._renderInputWithRefForward(),
        keepFocus: false
      }))), /*#__PURE__*/React.createElement(Popover.Content, null, /*#__PURE__*/React.createElement("div", {
        "data-hook": calendarDataHook
      }, /*#__PURE__*/React.createElement(Calendar, calendarProps))));
    }
  }]);

  return DatePicker;
}(React.PureComponent);

_defineProperty(DatePicker, "displayName", 'DatePicker');

_defineProperty(DatePicker, "defaultProps", {
  locale: 'en',
  filterDate: function filterDate() {
    return true;
  },
  rtl: false,
  width: 150,
  zIndex: 1,
  disabled: false,
  inputDataHook: 'date-picker-input',
  popoverProps: {
    placement: 'top-start',
    zIndex: 1
  },
  firstDayOfWeek: 1
});

export { DatePicker as default };
DatePicker.propTypes = _objectSpread(_objectSpread({}, Calendar.propTypes), {}, {
  /** Can provide Input with your custom props. If you don't need a custom input element, and only want to pass props to the Input, then use inputProps prop. I think this is not in use outside of WSR, and can be deprecated. */
  customInput: PropTypes.node,

  /** Properties appended to the default Input component or the custom Input component. */
  inputProps: PropTypes.object,

  /** this prop is deprecated and should not be used
   * @deprecated
   */
  dateFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /** Custom date format V2, can be either:
   * * `string` of tokens (see [`date-fns V2` docs](https://date-fns.org/v2.15.0/docs/format) for list of supported tokens)
   * * `function` of signature `Date -> String`
   */
  dateFormatV2: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  /** DatePicker instance locale */
  locale: PropTypes.oneOfType([PropTypes.oneOf(['en', 'es', 'pt', 'fr', 'de', 'pl', 'it', 'ru', 'ja', 'ko', 'tr', 'sv', 'no', 'nl', 'da', 'zh', 'th', 'cs']), PropTypes.shape({
    distanceInWords: PropTypes.object,
    format: PropTypes.object
  })]),

  /** Is the DatePicker disabled */
  disabled: PropTypes.bool,

  /** dataHook for the DatePicker's Input */
  inputDataHook: PropTypes.string,

  /** calendarDataHook for the DatePicker's calendar view */
  calendarDataHook: PropTypes.string,

  /** placeholder of the Input */
  placeholderText: PropTypes.string,

  /** RTL mode */
  rtl: PropTypes.bool,

  /** The selected date */
  value: PropTypes.object,

  /** Controls the whether the calendar will be initially visible or not */
  initialOpen: PropTypes.bool,

  /** Sets UI to indicate a status */
  status: PropTypes.oneOf(['error', 'warning', 'loading']),

  /** The status message to display when hovering the status icon, if not given or empty there will be no tooltip */
  statusMessage: PropTypes.node,

  /** set desired width of DatePicker input */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  /** set desired z-index of DatePicker popover */
  zIndex: PropTypes.number,
  popoverProps: PropTypes.shape(PopoverCommonProps),

  /** First day of the week, allowing only from 0 to 6 (Sunday to Saturday) */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6])
});